import React, { useState, useMemo } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Stack,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import axios from 'axios'
import useAccount from '../../hooks/useAccount'

// ============================================================================
// Types (matching compare.ts output)
// ============================================================================

interface ParityReport {
  generatedAt: string
  precision: number
  oldDir: string
  newDir: string
  summary: {
    totalTables: number
    matching: number
    different: number
    missingInOld: number
    missingInNew: number
    errorInOld: number
    errorInNew: number
  }
  tables: TableComparison[]
}

interface TableComparison {
  path: string
  status: 'match' | 'different' | 'missing_old' | 'missing_new' | 'error'
  differenceCount?: number
  differences?: Difference[]
  error?: string
}

interface Difference {
  path: string
  oldValue: unknown
  newValue: unknown
  type: 'value' | 'type' | 'missing' | 'extra' | 'structure'
}

type StatusFilter = 'all' | 'match' | 'different' | 'missing_old' | 'missing_new' | 'error'

// ============================================================================
// Helpers
// ============================================================================

const getStatusColor = (status: TableComparison['status']): 'success' | 'error' | 'warning' | 'default' => {
  switch (status) {
    case 'match':
      return 'success'
    case 'different':
      return 'error'
    case 'missing_old':
    case 'missing_new':
      return 'warning'
    case 'error':
      return 'error'
    default:
      return 'default'
  }
}

const getStatusIcon = (status: TableComparison['status']) => {
  switch (status) {
    case 'match':
      return <CheckCircleIcon fontSize="small" color="success" />
    case 'different':
      return <ErrorIcon fontSize="small" color="error" />
    case 'missing_old':
    case 'missing_new':
      return <WarningIcon fontSize="small" color="warning" />
    case 'error':
      return <ErrorIcon fontSize="small" color="error" />
    default:
      return <HelpOutlineIcon fontSize="small" />
  }
}

const getStatusLabel = (status: TableComparison['status']): string => {
  switch (status) {
    case 'match':
      return 'Match'
    case 'different':
      return 'Different'
    case 'missing_old':
      return 'Missing (Old)'
    case 'missing_new':
      return 'Missing (New)'
    case 'error':
      return 'Error'
    default:
      return status
  }
}

const formatValue = (value: unknown): string => {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (typeof value === 'string') return `"${value}"`
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

const truncatePath = (path: string, maxLength = 50): string => {
  if (path.length <= maxLength) return path
  return '...' + path.slice(-(maxLength - 3))
}

// ============================================================================
// Table Row Component
// ============================================================================

interface TableRowProps {
  table: TableComparison
}

const ParityTableRow: React.FC<TableRowProps> = ({ table }) => {
  const [open, setOpen] = useState(false)
  const hasDiffs = table.differences && table.differences.length > 0

  return (
    <>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          cursor: hasDiffs ? 'pointer' : 'default',
          '&:hover': hasDiffs ? { backgroundColor: 'action.hover' } : {},
        }}
        onClick={() => hasDiffs && setOpen(!open)}
      >
        <TableCell sx={{ width: 40 }}>
          {hasDiffs && (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open) }}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            {getStatusIcon(table.status)}
            <Chip
              label={getStatusLabel(table.status)}
              size="small"
              color={getStatusColor(table.status)}
              sx={{ minWidth: 90 }}
            />
          </Stack>
        </TableCell>
        <TableCell>
          <Tooltip title={table.path} placement="top-start">
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 400,
              }}
            >
              {table.path}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          {table.differenceCount !== undefined && (
            <Chip
              label={table.differenceCount > 50 ? `50+ (${table.differenceCount})` : table.differenceCount}
              size="small"
              variant="outlined"
              color={table.differenceCount > 0 ? 'error' : 'default'}
            />
          )}
        </TableCell>
      </TableRow>
      {hasDiffs && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Differences ({table.differenceCount})
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Path</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Old Value</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>New Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {table.differences?.map((diff, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Chip
                            label={diff.type}
                            size="small"
                            variant="outlined"
                            color={
                              diff.type === 'value' ? 'error' :
                              diff.type === 'structure' ? 'warning' :
                              diff.type === 'missing' ? 'info' :
                              diff.type === 'extra' ? 'success' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={diff.path}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.7rem',
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {truncatePath(diff.path)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box
                            component="pre"
                            sx={{
                              backgroundColor: '#ffebee',
                              p: 0.5,
                              borderRadius: 0.5,
                              overflow: 'auto',
                              fontSize: '0.65rem',
                              maxHeight: 100,
                              margin: 0,
                            }}
                          >
                            {formatValue(diff.oldValue)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            component="pre"
                            sx={{
                              backgroundColor: '#e8f5e9',
                              p: 0.5,
                              borderRadius: 0.5,
                              overflow: 'auto',
                              fontSize: '0.65rem',
                              maxHeight: 100,
                              margin: 0,
                            }}
                          >
                            {formatValue(diff.newValue)}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {table.differenceCount && table.differenceCount > 50 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Showing first 50 of {table.differenceCount} differences
                  </Typography>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

// ============================================================================
// Main Component
// ============================================================================

interface AvailableReport {
  customer: string
  job: string
}

const ParityCheckExperiment: React.FC = () => {
  const account = useAccount()
  const [report, setReport] = useState<ParityReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [availableReports, setAvailableReports] = useState<AvailableReport[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState('')
  const [selectedJob, setSelectedJob] = useState('')

  // Get customers from session info for dropdown
  const customers = account.user?.session_info?.sessionCusts || []

  // Get jobs for selected customer
  const jobs = useMemo(() => {
    const customer = customers.find(c => c.name === selectedCustomer)
    return customer?.sessionJobs || []
  }, [customers, selectedCustomer])

  // Load list of available parity reports on mount
  const loadAvailableReports = async () => {
    try {
      const response = await axios.get<{ reports: AvailableReport[] }>('/api/v1/dev/parity-reports')
      setAvailableReports(response.data.reports || [])
    } catch {
      // Silently fail - reports list is optional
    }
  }

  // Check if a report is available for the selected customer/job
  const reportAvailable = useMemo(() => {
    return availableReports.some(r => r.customer === selectedCustomer && r.job === selectedJob)
  }, [availableReports, selectedCustomer, selectedJob])

  const loadReport = async () => {
    if (!selectedCustomer || !selectedJob) {
      setError('Please select a customer and job')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Uses axios which has the JWT token configured globally by the account context
      const response = await axios.get<ParityReport>(`/api/v1/dev/parity-report/${selectedCustomer}/${selectedJob}`)
      setReport(response.data)
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorMessage = err.response?.data?.error || err.message
        setError(errorMessage)
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load report')
      }
    } finally {
      setLoading(false)
    }
  }

  // Load available reports on mount
  React.useEffect(() => {
    loadAvailableReports()
  }, [])

  const filteredTables = useMemo(() => {
    if (!report) return []

    return report.tables.filter((table) => {
      // Status filter
      if (statusFilter !== 'all' && table.status !== statusFilter) {
        return false
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        if (!table.path.toLowerCase().includes(query)) {
          return false
        }
      }

      return true
    })
  }, [report, statusFilter, searchQuery])

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Carbon-Go Parity Check</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Compare C# Carbon output with Go carbon-go output to verify parity between systems.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Reports embedded at:</strong> <code>goapi/pkg/server/parity-reports/{'<customer>'}/{'<job>'}.json</code>
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <strong>To generate:</strong> <code>cd tests/parity-check && yarn compare --old ./data/womens-football/run-old --new ./data/womens-football/run-new --toc ./data/womens-football/toc.json --output ./data/womens-football/parity-report.json</code>
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Then copy to: <code>goapi/pkg/server/parity-reports/bprtesting/womens-football.json</code>
        </Typography>
        {availableReports.length > 0 && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Available reports:</strong> {availableReports.map(r => `${r.customer}/${r.job}`).join(', ')}
          </Typography>
        )}
      </Alert>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Customer</InputLabel>
          <Select
            value={selectedCustomer}
            label="Customer"
            onChange={(e) => {
              setSelectedCustomer(e.target.value)
              setSelectedJob('')
              setReport(null)
            }}
          >
            {customers.map((customer) => (
              <MenuItem key={customer.name || ''} value={customer.name || ''}>
                {customer.displayName || customer.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }} disabled={!selectedCustomer}>
          <InputLabel>Job</InputLabel>
          <Select
            value={selectedJob}
            label="Job"
            onChange={(e) => {
              setSelectedJob(e.target.value)
              setReport(null)
            }}
          >
            {jobs.map((job) => (
              <MenuItem key={job.name || ''} value={job.name || ''}>
                {job.displayName || job.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="info"
          onClick={loadReport}
          disabled={loading || !selectedCustomer || !selectedJob}
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          {loading ? 'Loading...' : report ? 'Reload Report' : 'Load Report'}
        </Button>
        {selectedCustomer && selectedJob && !reportAvailable && availableReports.length > 0 && (
          <Chip label="No report available" color="warning" size="small" />
        )}
        {selectedCustomer && selectedJob && reportAvailable && (
          <Chip label="Report available" color="success" size="small" />
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {report && (
        <>
          {/* Summary */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip label={`Total: ${report.summary.totalTables}`} />
            <Chip label={`Match: ${report.summary.matching}`} color="success" />
            <Chip label={`Different: ${report.summary.different}`} color="error" />
            <Chip label={`Missing Old: ${report.summary.missingInOld}`} color="warning" variant="outlined" />
            <Chip label={`Missing New: ${report.summary.missingInNew}`} color="warning" variant="outlined" />
            {report.summary.errorInOld > 0 && (
              <Chip label={`Error Old: ${report.summary.errorInOld}`} color="error" variant="outlined" />
            )}
            {report.summary.errorInNew > 0 && (
              <Chip label={`Error New: ${report.summary.errorInNew}`} color="error" variant="outlined" />
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Generated: {new Date(report.generatedAt).toLocaleString()} | Precision: {report.precision} decimals
          </Typography>

          {/* Filters */}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="match">Match</MenuItem>
                <MenuItem value="different">Different</MenuItem>
                <MenuItem value="missing_old">Missing (Old)</MenuItem>
                <MenuItem value="missing_new">Missing (New)</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Search by path"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flex: 1, maxWidth: 400 }}
            />
          </Stack>

          {/* Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell sx={{ fontWeight: 'bold', width: 130 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Table Path</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 100 }} align="right">Diffs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTables.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary">No tables match the current filters</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTables.map((table) => (
                    <ParityTableRow key={table.path} table={table} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Showing {filteredTables.length} of {report.tables.length} tables
          </Typography>
        </>
      )}
    </Paper>
  )
}

export default ParityCheckExperiment
